import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  MutateVariablePayload,
  VariableDto,
  VariableSourceSettingsDto,
} from '@thingslog/repositories';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableForm, VariableValueForm } from '../../forms';
import { DashboardSidebarWidgetCard } from './DashboardSidebarWidgetCard';

interface Props {
  widgetId: string;
  variable: VariableDto | null;
  variableSources: VariableSourceSettingsDto[];
  companyId?: number | null;
  isPersisting: boolean;
  onPersistVariable: (
    id: string,
    payload: MutateVariablePayload,
    callbacks?: { onSuccess?: () => void }
  ) => void;
  onTempSaveVariable: (id: string, payload: MutateVariablePayload) => void;
  onClose: (widgetId: string) => void;
  onStartTour: () => void;
}

type VariableEditMode = 'local' | 'remote';

export const DashboardSidebarVariableEditWidget: FC<Props> = ({
  widgetId,
  variable,
  variableSources,
  companyId,
  isPersisting,
  onPersistVariable,
  onTempSaveVariable,
  onClose,
  onStartTour,
}: Props) => {
  const { t } = useTranslation();
  const [variableEditMode, setVariableEditMode] = useState<VariableEditMode>('local');

  if (!variable) {
    return (
      <DashboardSidebarWidgetCard
        title={t('dashboard_edit_variable', 'Edit Variable')}
        widgetId={widgetId}
        onClose={onClose}
        onStartTour={onStartTour}
      >
        <div className="p-4 text-sm text-gray-600">
          {t('dashboard_variable_no_longer_exists', 'Variable no longer exists.')}
        </div>
      </DashboardSidebarWidgetCard>
    );
  }

  return (
    <DashboardSidebarWidgetCard
      title={t('dashboard_edit_named_variable', 'Edit {{name}}', {
        name: variable.label || variable.name,
      })}
      widgetId={widgetId}
      expandable
      onClose={onClose}
      onStartTour={onStartTour}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 px-4 pt-4">
          <Typography variant="body2" className="text-gray-600">
            {t(
              'dashboard_variable_edit_mode_description',
              'Local changes only affect this dashboard view for now. Remote changes update the saved variable and replace any local override for it.'
            )}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={variableEditMode}
            onChange={(_: React.MouseEvent<HTMLElement>, value: VariableEditMode | null): void => {
              if (value) {
                setVariableEditMode(value);
              }
            }}
          >
            <ToggleButton value="local">{t('dashboard_local', 'Local')}</ToggleButton>
            <ToggleButton value="remote">{t('dashboard_remote', 'Remote')}</ToggleButton>
          </ToggleButtonGroup>
        </div>

        {variableEditMode === 'remote' ? (
          <VariableForm
            type="edit"
            variableSources={variableSources}
            companyId={companyId}
            defaultValues={variable}
            disabled={isPersisting}
            onSubmit={(id: string, payload: MutateVariablePayload): void => {
              onPersistVariable(id, payload, { onSuccess: () => onClose(widgetId) });
            }}
            onCancel={(): void => {
              onClose(widgetId);
            }}
          />
        ) : (
          <VariableValueForm
            variable={variable}
            variableSources={variableSources}
            companyId={companyId}
            onSubmit={(id: string, payload: MutateVariablePayload): void => {
              onTempSaveVariable(id, payload);
              onClose(widgetId);
            }}
            onCancel={(): void => {
              onClose(widgetId);
            }}
          />
        )}
      </div>
    </DashboardSidebarWidgetCard>
  );
};
